/**
 * PrivacySection — Settings → Privacy & data
 *
 * The consent records the user agreed to at signup, plus the self-serve
 * version of the access right the Privacy Policy promises (§10: "access the
 * personal information we hold about you").
 *
 * My Notes rebuild, Stage 5 (Directional Change 11): notes now live on
 * JAI's servers, so accountApi.exportData()'s response already includes
 * them (see accountController.js's exportData) — this file no longer
 * scrapes localStorage to assemble the download. The OLD version's whole
 * point was making "your notes only exist on this device" visible; that
 * claim is no longer true, so this section says the opposite now.
 */

import { useState } from 'react'
import { accountApi } from '../../services/api'
import { JAI_COLORS } from '../JaiBrand'
import { GhostButton } from '../ModalPortal'
import { SectionNote, Notice, FieldError, formatDate } from './SettingsFields'
import { IS_NATIVE_APP } from '../../services/runtime'

/**
 * A ticked, read-only checkbox showing something the user agreed to.
 *
 * Deliberately not un-tickable: agreeing to the Terms and Privacy Policy is a
 * condition of having a JAI account, so there's nothing to toggle. To withdraw
 * consent you delete the account. `aria-disabled` tells screen readers the same
 * thing the greyed-out styling tells everyone else.
 */
/**
 * `acceptedAt` of null means genuinely not accepted — currently only a guest
 * session, which never had a form with a terms checkbox on it. The row then
 * shows an EMPTY box and says so. Ticking it with a date anyway would be a
 * fabricated consent record on the one screen whose whole job is to report
 * what the user actually agreed to, and when.
 */
function ConsentRow({ title, acceptedAt, timezone, linkLabel, href }) {
  const accepted = !!acceptedAt
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '15px 0', borderBottom: `1px solid ${JAI_COLORS.hair}` }}>
      <span
        role="checkbox"
        aria-checked={accepted ? 'true' : 'false'}
        aria-disabled="true"
        aria-label={`${title} — ${accepted ? 'accepted' : 'not yet accepted'}`}
        style={{
          width: 19, height: 19, borderRadius: 5, flexShrink: 0, marginTop: 1,
          background: accepted ? JAI_COLORS.blue : 'transparent',
          border: `1.5px solid ${accepted ? JAI_COLORS.blue : JAI_COLORS.hair}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {accepted && (
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
            <path d="M2.5 8.5l3.5 3.5 7.5-8" stroke="white" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: JAI_COLORS.navy }}>{title}</div>
        <div style={{ fontSize: 12.5, color: JAI_COLORS.mute, marginTop: 3 }}>
          {accepted ? `Accepted ${formatDate(acceptedAt, timezone)}` : 'Not yet accepted'}
        </div>
      </div>

      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ flexShrink: 0, fontSize: 12.5, fontWeight: 600, color: JAI_COLORS.blue, textDecoration: 'none' }}
      >
        {linkLabel} ↗
      </a>
    </div>
  )
}

export default function PrivacySection({ details }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  const download = async () => {
    setBusy(true)
    setError('')
    try {
      const { data } = await accountApi.exportData()

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const stamp = new Date().toISOString().slice(0, 10)

      const link = document.createElement('a')
      link.href = url
      link.download = `jai-my-data-${stamp}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      setDone(true)
      setTimeout(() => setDone(false), 6000)
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not build your export. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  // Only fall back to the signup date for REAL accounts: those predate the
  // termsAcceptedAt column but still had to tick the box to get in. A guest's
  // null is the truth, not a gap to paper over, so it's passed straight
  // through — see the ConsentRow comment above.
  const acceptedAt = details.isGuest ? null : (details.termsAcceptedAt || details.createdAt)

  return (
    <div>
      <ConsentRow
        title="Terms of Service"
        acceptedAt={acceptedAt}
        timezone={details.timezone}
        linkLabel="Read"
        href="/terms"
      />
      <ConsentRow
        title="Privacy Policy"
        acceptedAt={acceptedAt}
        timezone={details.timezone}
        linkLabel="Read"
        href="/privacy"
      />
      <SectionNote>
        {details.isGuest
          ? "You're browsing as a guest, so you haven't been asked to accept these yet. You'll agree to both when you create your account."
          : "You accepted both when you created your JAI account — they're required to use the app, so they can't be switched off here. To withdraw your agreement, delete your account from the Account tab."}
      </SectionNote>

      {/* ── Download your data ───────────────────────────────────────────── */}
      <div style={{ marginTop: 26, paddingTop: 20, borderTop: `1px solid ${JAI_COLORS.hair}` }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: JAI_COLORS.navy }}>
          Download your data
        </div>
        <SectionNote>
          A JSON file with everything JAI holds about you: your account details
          and preferences, your connected leagues, any support messages you've
          sent, and your My Notes.
        </SectionNote>

        {done && (
          <Notice tone="good">
            Downloaded. Keep it somewhere safe — it has your account details in it.
          </Notice>
        )}

        <div style={{ marginTop: 14 }}>
          <GhostButton onClick={download} disabled={busy}>
            {busy ? 'Preparing…' : 'Download my data'}
          </GhostButton>
          <FieldError>{error}</FieldError>
        </div>
      </div>

      {/* ── Where things are kept ────────────────────────────────────────── */}
      <div style={{ marginTop: 26, paddingTop: 20, borderTop: `1px solid ${JAI_COLORS.hair}` }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: JAI_COLORS.navy }}>
          Where your data lives
        </div>

        <SectionNote>
          Your account, leagues, preferences and My Notes are all stored on
          JAI's servers — sign in anywhere and they're there, including your
          notes. Deleting your account deletes all of it.
          {!IS_NATIVE_APP && (
            <> Card details are never held by JAI: payments run through
            Stripe, and invoices live in the billing portal under the
            Subscription tab.</>
          )}
        </SectionNote>
      </div>
    </div>
  )
}
